import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { and, notEq } from 'ember-truth-helpers';
import { LinkTo } from '@ember/routing';
import type Owner from '@ember/owner';
import ItemDetail from '../../components/item-detail/index.gts';
import MentionCard from '../../components/mention-card.gts';
import MoreActionsMenu from '../../components/more-actions-menu.gts';
import ItemActions from '../../components/item-actions.gts';
import TypeBadge from '../../components/type-badge.gts';
import ItemCoverHero from '../../components/item-cover-hero.gts';
import type { Item } from '../../schemas/item.ts';
import type { ItemStats } from '../../schemas/item-stats.ts';
import type { Mention } from '../../schemas/mention.ts';
import tw from '../../helpers/tw';
import MobileHeader from '../../components/mobile-header.gts';
import pullToRefresh from '../../modifiers/pull-to-refresh.ts';
import type Language from '../../services/language';
import type Session from '../../services/session';
import { fetchUserPreferences } from '../../utils/preferences';

interface Signature {
  Args: {
    model: {
      item: Item | null;
      stats: ItemStats | null;
      mentions: Mention[];
      distance?: string | null;
      refresh?: () => unknown;
    };
  };
}

export default class ItemsShowTemplate extends Component<Signature> {
  @service declare language: Language;
  @service declare session: Session;

  @tracked watchRegion = '';
  @tracked streamingServices: number[] = [];

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    void this.loadPreferences();
  }

  async loadPreferences() {
    const prefs = await fetchUserPreferences(this.session.authHeader);
    this.watchRegion = prefs.watchRegion;
    this.streamingServices = prefs.streamingServices;
  }

  @action async refresh() {
    await this.args.model.refresh?.();
  }

  get enrichedTitle(): string {
    const item = this.args.model.item;
    if (!item) return '';

    const enriched = item.enrichedData as
      { title?: string; name?: string } | null | undefined;

    return enriched?.title || enriched?.name || item.name || '';
  }

  get itemEmoji(): string {
    const item = this.args.model.item;
    if (!item || item.type !== 'place') return '';
    return item.emoji ?? '';
  }

  get hasConsensus(): boolean {
    const count = this.args.model.stats?.opinionCount ?? 0;
    return count >= 3;
  }

  <template>
    <div {{pullToRefresh this.refresh}}>
      {{#if @model.item}}
        <MobileHeader
          @scrollOpacity={{true}}
          @back={{true}}
          @noSpacer={{true}}
          @title={{this.enrichedTitle}}
        >
          <div class="ml-auto">
            <MoreActionsMenu @targetType="item" @targetId={{@model.item.id}} />
          </div>
        </MobileHeader>

        <div class="mb-4 hidden items-center justify-between lg:flex">
          <LinkTo @route="items" class="btn btn-ghost btn-sm gap-1">
            &larr; Back to my items
          </LinkTo>
          <MoreActionsMenu @targetType="item" @targetId={{@model.item.id}} />
        </div>

        {{! Full-width cover hero with title + poster (Letterboxd layout) }}
        <ItemCoverHero
          @item={{@model.item}}
          @title={{this.enrichedTitle}}
          @emoji={{this.itemEmoji}}
        >
          {{! Badges below the title, inside the hero }}
          <div class="mt-2 flex min-w-0 flex-wrap gap-2">
            <TypeBadge @type={{@model.item.type}} />
            {{#if
              (and
                @model.item.language
                (notEq @model.item.language this.language.preferredLanguage)
              )
            }}
              <span class="badge badge-outline">
                {{@model.item.language}}
              </span>
            {{/if}}
            {{#if @model.stats.opinionCount}}
              <span class="badge badge-ghost" title="Opinion mentions">
                {{@model.stats.opinionCount}}
                opinions
              </span>
            {{/if}}
            {{#if @model.stats.contextualCount}}
              <span
                class="badge badge-ghost badge-outline"
                title="Contextual references"
              >
                {{@model.stats.contextualCount}}
                references
              </span>
            {{/if}}
            {{#if this.hasConsensus}}
              <span
                class={{tw "badge" @model.stats.sentimentClass}}
                title="Recommendation consensus (opinions only)"
              >
                🍅
                {{@model.stats.recommendationPct}}%
              </span>
            {{/if}}
          </div>
        </ItemCoverHero>

        {{! Actions + details — below the hero }}
        <div class="px-4 sm:px-6">
          {{! Actions }}
          <div class="mt-3 flex gap-2">
            <ItemActions @itemId={{@model.item.id}} />
          </div>

          {{! Type-specific enrichment details }}
          <div class="mt-6">
            <ItemDetail
              @item={{@model.item}}
              @distance={{@model.distance}}
              @watchRegion={{this.watchRegion}}
              @subscribedServices={{this.streamingServices}}
            />
          </div>
        </div>

        {{! Mentions list }}
        <div class="px-4 sm:px-6">
          <h2 class="mt-8 mb-4 text-2xl font-bold">Mentions</h2>
          {{#if @model.mentions.length}}
            <div class="flex flex-col gap-4">
              {{#each @model.mentions as |mention|}}
                <MentionCard @mention={{mention}} @hideItem={{true}} />
              {{/each}}
            </div>
          {{else}}
            <div class="alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="h-6 w-6 shrink-0 stroke-current"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>No mentions found for this item.</span>
            </div>
          {{/if}}
        </div>
      {{else}}
        <div class="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <span>Item not found</span>
        </div>
      {{/if}}
    </div>
  </template>
}
